import { useCallback, useMemo } from 'react'
import { generatePath, useParams } from 'react-router-dom'

import { Layout } from 'admin/lib/Layouts'
import { Breadcrumb, BreadcrumbArrayItem } from 'admin/lib/Breadcrumbs'
import { useApiQuery } from 'admin/lib/Api'
import { ScreenSpin } from 'admin/lib/ScreenSpin'
import * as ROUTES from 'admin/routes'
import { Alert } from 'antd'
import { EditOneClickLca } from './EditOneClickLca'

export type Project = {
  projectId: string
  name: string
  moduleTags: { moduleTagDefinitionId: string; isTagged: boolean }
  country: string
  timeZone: string
  isOneClickLcaEnabled: boolean
  isProLicence: boolean
}

type Account = {
  name: string
}

type Context = {
  loaded: boolean
  account: Account | null
  projects: Project[] | null
  loadingError: string | null
  loadingErrorFor: string | null
}

export function OneClickLca() {
  const { accountId } = useParams<{
    accountId: string
  }>()

  const account = useApiQuery(
    useCallback(
      api => {
        if (!accountId) {
          throw new Error('Account ID is required')
        }
        return api.getAccount(accountId)
      },
      [accountId]
    )
  )

  const projects = useApiQuery(
    useCallback(
      api => {
        if (!accountId) {
          throw new Error('Account ID is required')
        }
        return api.listProjects(accountId)
      },
      [accountId]
    )
  )

  const context = useMemo(() => {
    return {
      loaded: account.loaded && projects.loaded,
      account: account.data,
      projects: projects.data,
      loadingError: account.error || projects.error,
      loadingErrorFor: account.error
        ? 'Account'
        : projects.error
          ? 'Account Projects'
          : null
    } as Context
  }, [
    account.data,
    account.error,
    account.loaded,
    projects.data,
    projects.error,
    projects.loaded
  ])

  return (
    <Layout
      header={
        <Breadcrumb
          items={[
            {
              title: (
                <BreadcrumbArrayItem
                  href={generatePath(ROUTES.ACCOUNTS.LIST)}
                  loading={!account.loaded}
                >
                  Accounts
                </BreadcrumbArrayItem>
              )
            },
            {
              title: (
                <BreadcrumbArrayItem
                  loading={!context.loaded}
                  href={generatePath(ROUTES.PROJECTS.LIST, {
                    accountId: accountId
                  })}
                >
                  {context.account?.name}
                </BreadcrumbArrayItem>
              )
            },
            {
              title: (
                <BreadcrumbArrayItem loading={false} href={null}>
                  One Click LCA Integration
                </BreadcrumbArrayItem>
              )
            }
          ]}
        />
      }
    >
      <>
        {!context.loaded && <ScreenSpin />}

        {context.loadingError && (
          <Alert
            type="error"
            message={`${context.loadingErrorFor} failed to load`}
            description={context.loadingError}
            showIcon
            style={{ marginTop: '20px' }}
          />
        )}

        {context.loaded && !context.loadingError && (
          <EditOneClickLca projects={context.projects!} />
        )}
      </>
    </Layout>
  )
}
